/* eslint-disable react-hooks/exhaustive-deps */
import { User } from 'firebase/auth';
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileRole from '../entities/profile-role';
import AuthenticationError from '../errors/authentication.error';
import { usePersons } from '../hooks';
import useAuthentication from '../hooks/providers/useAuthentication';

interface UserAuthenticated extends Omit<User, 'photoURL'> {
  photoUrl?: string | null;
  name?: string;
}

interface AuthContextData {
  signed: boolean;
  user: UserAuthenticated | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function useAuthContext() {
  return useContext(AuthContext);
}

type EncodeUser = UserAuthenticated & Omit<User, 'photoURL'>;

function encodeUser(user: EncodeUser): UserAuthenticated {
  return {
    ...user,
    name: (user.name ?? user.displayName)!,
  };
}

export const AuthProvider: React.FC = ({ children }) => {
  const history = useNavigate();
  const auth = useAuthentication();
  const persons = usePersons();

  const getSessionUser = useCallback((): UserAuthenticated | null => {
    const localUser = localStorage.getItem('sessionUser');

    if (!localUser) {
      return null;
    }

    return JSON.parse(localUser);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await auth.getIdTokenResult(getSessionUser() as User);
  }, []);

  const [loading, setLoading] = useState(false);
  const [signed, setSigned] = useState(Boolean(getSessionUser()));

  const logout = useCallback(async () => {
    await auth.signOut();
    localStorage.removeItem('sessionUser');
    history('/');
  }, [auth, history]);

  const manageUser = useCallback(async (newUser: UserAuthenticated | null) => {
    if (!newUser) {
      logout();
      setSigned(false);
      return;
    }

    const user = encodeUser({ ...getSessionUser(), ...newUser });
    localStorage.setItem('sessionUser', JSON.stringify(user));
    setSigned(true);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      const response = await auth.signInWithEmailAndPassword({ email, password });

      const profiles = await persons.fetchProfilesOf(response.user.uid);
      const managerProfile = profiles.find((profile) => profile.role === ProfileRole.Manager);

      if (!managerProfile) {
        throw new AuthenticationError('Não é possivel logar com outros perfis');
      }

      const customToken = await auth.createCustomToken(managerProfile.id);
      await auth.signWithCustomToken(customToken.token);
      const loadedUser = await persons.fetchById(response.user.uid);

      manageUser(encodeUser({ ...response.user, name: loadedUser.name, photoUrl: loadedUser.photoUrl }));
      setLoading(false);
    },
    [auth]
  );

  useEffect(() => {
    refresh();
    auth.onStateChange(manageUser);

    if (signed) {
      refresh();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ signed, user: getSessionUser(), loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
